import React from "react";
import { Download, X, ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayProductName } from "@/lib/productName";

const CURRENCY_SYMBOLS = { USD: "$", EUR: "€", GBP: "£", TRY: "₺" };
const fmtMoney = (cents, currency = "USD") => {
  const sym = CURRENCY_SYMBOLS[currency?.toUpperCase()] || `${currency} `;
  const val = (Number(cents || 0)) / 100;
  return `${sym}${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const centsOf = (inv, key) => {
  // Prefer the explicit *_cents field written by the current webhook path.
  if (inv[`${key}_cents`] != null) return Number(inv[`${key}_cents`]);
  // Legacy invoices only have the decimal-dollar mirror. Multiply by 100.
  if (inv[key] != null) {
    const raw = Number(inv[key]);
    // Defensive sanity check for the historical bug LP-2026-1784655156173:
    // pre-fix ``_money()`` mis-persisted integer cents as dollars, so
    // {subtotal:1, tax:20, total:21} means tax=$0.20, total=$1.20 — NOT
    // $20/$21. If a top-level tax or total value is implausibly larger
    // than the subtotal AND integer-looking, treat it as cents directly.
    if ((key === "tax" || key === "total") && Number.isInteger(raw) && raw >= 1) {
      const sub = Number(inv.subtotal ?? (Number(inv.subtotal_cents ?? 0) / 100));
      const tax = Number(inv.tax ?? (Number(inv.tax_cents ?? 0) / 100));
      if (sub > 0 && (key === "tax" ? raw : raw - sub) > sub * 0.5) {
        return raw; // treat legacy int as cents already
      }
    }
    return Math.round(raw * 100);
  }
  return 0;
};

function StatusBadge({ inv, t }) {
  const refund = (inv.refund_status || "").toLowerCase();
  const dispute = (inv.dispute_status || "").toLowerCase();
  if (dispute === "lost" || inv.status === "chargeback") {
    return <span className="lp-chip red" data-testid="badge-chargeback">{t("dash.billing.badge.chargeback")}</span>;
  }
  if (dispute === "open" || inv.status === "disputed") {
    return <span className="lp-chip amber" data-testid="badge-disputed">{t("dash.billing.badge.disputed")}</span>;
  }
  if (refund === "full" || inv.status === "refunded") {
    return <span className="lp-chip red" data-testid="badge-refunded">{t("dash.billing.badge.refunded")}</span>;
  }
  if (refund === "partial" || inv.status === "partially_refunded") {
    return <span className="lp-chip amber" data-testid="badge-partial">{t("dash.billing.badge.partial")}</span>;
  }
  return (
    <span className={`lp-chip ${inv.status === "paid" ? "green" : "amber"}`}>
      {t(`dash.billing.status.${inv.status || "paid"}`)}
    </span>
  );
}

export default function DashBilling() {
  const { t } = useLang();
  const [invoices, setInvoices] = React.useState(null);
  const [selected, setSelected] = React.useState(null);
  const [frozen, setFrozen] = React.useState(false);

  const load = React.useCallback(() => {
    api.get("/api/dashboard/invoices").then((r) => {
      setInvoices(r.data.items);
      setFrozen(!!r.data.topup_frozen);
    });
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const downloadPdf = async (inv) => {
    try {
      const resp = await api.get(`/api/dashboard/invoices/${inv.id}/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `LoftPress-PurchaseSummary-${inv.invoice_number || inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      const external = inv.invoice_url || inv.receipt_url;
      if (external) { window.open(external, "_blank", "noopener"); return; }
      alert(t("dash.billing.download_error"));
    }
  };

  if (!invoices) return <div className="h-40 lp-shimmer rounded-2xl" />;

  return (
    <div className="space-y-6" data-testid="dash-billing-page">
      <div>
        <h1 className="font-display text-3xl text-white/95">{t("dash.billing.title")}</h1>
        <p className="text-white/50 text-sm mt-1">{t("dash.billing.subtitle")}</p>
        <div className="mt-3 rounded-xl border border-amber-500/25 bg-amber-500/5 px-4 py-3 text-xs text-amber-100/85" data-testid="mor-notice-banner">
          {t("dash.billing.mor_notice")}
        </div>
        {frozen && (
          <div className="mt-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs text-red-100 flex items-start gap-2" data-testid="topup-freeze-banner">
            <AlertTriangle className="w-4 h-4 mt-[1px] shrink-0" />
            <span>{t("dash.billing.freeze_banner")}</span>
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="lp-card overflow-hidden hidden md:block">
        <table className="lp-table">
          <thead>
            <tr>
              <th>{t("dash.billing.col.invoice")}</th>
              <th>{t("dash.billing.col.date")}</th>
              <th>{t("dash.billing.col.product")}</th>
              <th className="text-right">{t("dash.billing.col.subtotal")}</th>
              <th className="text-right">{t("dash.billing.col.tax")}</th>
              <th className="text-right">{t("dash.billing.col.total")}</th>
              <th>{t("dash.billing.col.status")}</th>
              <th>{t("dash.billing.col.provider")}</th>
              <th className="text-right">{t("dash.billing.col.action")}</th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={9} className="text-center text-white/50 py-10">{t("dash.billing.empty")}</td></tr>
            )}
            {invoices.map((inv) => {
              const ptKey = (inv.purchase_type || inv.kind) === "credits" ? "credits" : "subscription";
              const refundedCents = Number(inv.refunded_amount_cents || 0);
              return (
                <tr key={inv.id} data-testid={`invoice-${inv.invoice_number || inv.number}`}>
                  <td className="font-mono text-white/90 text-xs">{inv.invoice_number || inv.number}</td>
                  <td className="text-white/60 whitespace-nowrap">{new Date(inv.created_at).toLocaleDateString()}</td>
                  <td className="text-white/80">
                    <div>{inv.product_name || (ptKey === "credits" ? t("dash.billing.type.credits") : t("dash.billing.type.subscription"))}</div>
                    <div className="text-white/40 text-xs">{t(`dash.billing.type.${ptKey}`)} · qty {inv.quantity || 1}</div>
                  </td>
                  <td className="text-right text-white/80">{fmtMoney(centsOf(inv, "subtotal"), inv.currency)}</td>
                  <td className="text-right text-white/70">{fmtMoney(centsOf(inv, "tax"), inv.currency)}</td>
                  <td className="text-right font-medium text-white/95">
                    <div>{fmtMoney(centsOf(inv, "total"), inv.currency)} {inv.currency}</div>
                    {refundedCents > 0 && (
                      <div className="text-red-300/80 text-[10px]" data-testid={`refunded-${inv.invoice_number || inv.number}`}>
                        −{fmtMoney(refundedCents, inv.currency)} {t("dash.billing.detail.refunded").toLowerCase()}
                      </div>
                    )}
                  </td>
                  <td><StatusBadge inv={inv} t={t} /></td>
                  <td className="text-white/60 capitalize">
                    {inv.provider || inv.payment_provider}
                    <span className="ml-1 text-[10px] uppercase tracking-wide text-white/40">
                      {(inv.environment || inv.provider_mode) === "live" ? t("dash.billing.env.live") : t("dash.billing.env.test")}
                    </span>
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        className="lp-btn-secondary text-xs py-1.5 px-3"
                        onClick={() => setSelected(inv)}
                        data-testid={`view-${inv.invoice_number || inv.number}`}
                      >{t("dash.billing.view")}</button>
                      <button
                        className="lp-btn-secondary text-xs py-1.5 px-3"
                        onClick={() => downloadPdf(inv)}
                        data-testid={`download-${inv.invoice_number || inv.number}`}
                      >
                        <Download className="w-3.5 h-3.5" /> {t("dash.billing.download")}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked cards */}
      <div className="md:hidden space-y-3" data-testid="dash-billing-mobile">
        {invoices.length === 0 && (
          <div className="lp-card p-6 text-center text-white/50">{t("dash.billing.empty")}</div>
        )}
        {invoices.map((inv) => {
          const ptKey = (inv.purchase_type || inv.kind) === "credits" ? "credits" : "subscription";
          return (
            <div key={inv.id} className="lp-card p-4" data-testid={`invoice-m-${inv.invoice_number || inv.number}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs font-mono text-white/80">{inv.invoice_number || inv.number}</div>
                  <div className="text-white/50 text-xs">{new Date(inv.created_at).toLocaleDateString()}</div>
                </div>
                <StatusBadge inv={inv} t={t} />
              </div>
              <div className="mt-2 text-white/85">{displayProductName(inv.product_name) || t(`dash.billing.type.${ptKey}`)}</div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <div><div className="text-white/40">{t("dash.billing.col.subtotal")}</div><div>{fmtMoney(centsOf(inv, "subtotal"), inv.currency)}</div></div>
                <div><div className="text-white/40">{t("dash.billing.col.tax")}</div><div>{fmtMoney(centsOf(inv, "tax"), inv.currency)}</div></div>
                <div><div className="text-white/40">{t("dash.billing.col.total")}</div><div className="text-white/95 font-medium">{fmtMoney(centsOf(inv, "total"), inv.currency)}</div></div>
              </div>
              {Number(inv.refunded_amount_cents || 0) > 0 && (
                <div className="mt-2 text-[11px] text-red-300/80">
                  {t("dash.billing.detail.refunded")}: −{fmtMoney(Number(inv.refunded_amount_cents), inv.currency)}
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button className="lp-btn-secondary text-xs py-1.5 px-3 flex-1" onClick={() => setSelected(inv)}>{t("dash.billing.view")}</button>
                <button className="lp-btn-secondary text-xs py-1.5 px-3 flex-1" onClick={() => downloadPdf(inv)}>
                  <Download className="w-3.5 h-3.5" /> {t("dash.billing.download")}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="invoice-detail-modal">
          <div className="lp-card max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white/50 text-xs uppercase tracking-wide">{t("dash.billing.detail.title")}</div>
                <div className="font-mono text-white/95 text-sm mt-1">{selected.invoice_number || selected.number}</div>
              </div>
              <button className="lp-btn-secondary p-1.5" onClick={() => setSelected(null)} data-testid="close-invoice-detail">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="rounded-lg border border-amber-500/25 bg-amber-500/5 px-3 py-2 text-[11px] leading-snug text-amber-100/85" data-testid="mor-disclaimer">
                {t("dash.billing.detail.disclaimer")}
              </div>
              <Row label={t("dash.billing.col.date")} value={new Date(selected.created_at).toLocaleString()} />
              <Row label={t("dash.billing.col.product")} value={displayProductName(selected.product_name) || t(`dash.billing.type.${(selected.purchase_type || selected.kind) === "credits" ? "credits" : "subscription"}`)} />
              <Row label={t("dash.billing.col.status")} valueSlot={<StatusBadge inv={selected} t={t} />} />
              <Row label={t("dash.billing.col.provider")} value={`${(selected.provider || selected.payment_provider || "").toUpperCase()} · ${((selected.environment || selected.provider_mode) === "live" ? t("dash.billing.env.live") : t("dash.billing.env.test"))}`} />
              <div className="my-3 border-t border-white/10" />
              <Row label={t("dash.billing.detail.qty")} value={selected.quantity ?? 1} />
              <Row label={t("dash.billing.detail.unit_price")} value={fmtMoney(centsOf(selected, "unit_price"), selected.currency)} />
              <Row label={t("dash.billing.col.subtotal")} value={fmtMoney(centsOf(selected, "subtotal"), selected.currency)} />
              <Row label={t("dash.billing.col.tax")} value={fmtMoney(centsOf(selected, "tax"), selected.currency)} />
              <Row label={t("dash.billing.col.total")} value={`${fmtMoney(centsOf(selected, "total"), selected.currency)} ${selected.currency || "USD"}`} bold />
              {Number(selected.refunded_amount_cents || 0) > 0 && (
                <>
                  <Row label={t("dash.billing.detail.refunded")}
                       value={`−${fmtMoney(Number(selected.refunded_amount_cents), selected.currency)}`}
                       highlight="red" />
                  <Row label={t("dash.billing.detail.net_paid")}
                       value={fmtMoney(Math.max(centsOf(selected, "total") - Number(selected.refunded_amount_cents), 0), selected.currency)}
                       bold />
                </>
              )}
            </div>

            {(selected.refund_events && selected.refund_events.length > 0) && (
              <div className="mt-4">
                <div className="text-white/50 text-xs uppercase tracking-wide mb-2">{t("dash.billing.detail.timeline")}</div>
                <ol className="space-y-2 text-xs" data-testid="refund-timeline">
                  {selected.refund_events.map((ev, idx) => (
                    <li key={ev.refund_id || idx} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-red-400 shrink-0" />
                      <div className="flex-1">
                        <div className="text-white/85">
                          {t("dash.billing.detail.refunded")}: −{fmtMoney(Number(ev.amount_cents || 0), selected.currency)}
                          {ev.credits_reversed ? ` · ${ev.credits_reversed.toLocaleString()} credits` : ""}
                        </div>
                        <div className="text-white/45">
                          {new Date(ev.created_at).toLocaleString()}
                          {ev.reason ? ` · ${ev.reason}` : ""}
                          {ev.status && ev.status !== "applied" ? ` · ${ev.status}` : ""}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="mt-5 flex gap-2">
              {(selected.invoice_url || selected.receipt_url) && (
                <a
                  className="lp-btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 flex-1 justify-center"
                  href={selected.invoice_url || selected.receipt_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="open-external-receipt"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> {t("dash.billing.external_receipt")}
                </a>
              )}
              <button className="lp-btn-secondary text-xs py-1.5 px-3 flex-1" onClick={() => downloadPdf(selected)}>
                <Download className="w-3.5 h-3.5" /> {t("dash.billing.download")} PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, valueSlot, bold, highlight }) {
  const colour = highlight === "red" ? "text-red-300" : (bold ? "text-white/95 font-semibold" : "text-white/85");
  return (
    <div className="flex justify-between items-baseline gap-4">
      <div className="text-white/50 text-xs">{label}</div>
      <div className={`text-right ${colour}`}>{valueSlot || value}</div>
    </div>
  );
}
